<template>
  <div class="container">
    <div class="columns">
      <div class="column ls-left-column">
        <div test-id="your_details">
          <h1>Your Details</h1>
          <div class="user-info">
            <LsInlineEdit
              :value="auth.user.first_name"
              track-id="profile_view-fname"
              test-id="fname"
              name="fname"
              label="first name"
              :loading="determineLoading('fname')"
              :validation="{ required: true }"
              @current-field="setCurrentField"
              @edit-success="updateProfile($event, 'fname')"
            />
            <LsInlineEdit
              :value="auth.user.last_name"
              track-id="profile_view-lname"
              test-id="lname"
              name="lname"
              label="last name"
              :validation="{ required: true }"
              :loading="determineLoading('lname')"
              @current-field="setCurrentField"
              @edit-success="updateProfile($event, 'lname')"
            />
            <LsInlineEdit
              :value="auth.user.email"
              track-id="profile_view-email"
              test-id="email"
              name="email"
              label="email"
              type="email"
              :loading="determineLoading('email')"
              :validation="{ required: true, email: true }"
              @current-field="setCurrentField"
              @edit-success="updateProfile($event, 'email')"
            />
            <LsInlineEdit
              :value="auth.user.phone"
              track-id="profile_view-phone"
              test-id="phone"
              name="phone"
              label="phone"
              type="tel"
              mask="phone"
              :loading="determineLoading('phone')"
              :validation="{ required: true }"
              :bottom-border="false"
              @current-field="setCurrentField"
              @edit-success="updateProfile($event, 'phone')"
            />
          </div>
          <div class="button-wrapper">
            <LsButton
              test-id="set_password"
              :text="`${updateOrSet} password`"
              track-id="profile_view-set_password"
              @click="openPassword"
            />
          </div>
        </div>

        <div v-if="!isCustomerLead" test-id="payment_details">
          <h1>Payment Details</h1>
          <div class="cc-info-wrapper">
            <div class="cc-info">
              <p class="has-text-weight-bold">Current Payment</p>
              <div v-if="auth.user.customer.last4" class="card-div">
                <LsIcon type="creditcard" size="lg" color="primary-light" />
                <div class="cc-info-text">
                  <span>
                    Card ending in
                    <span class="has-text-weight-bold" test-id="has_card_text">
                      {{ auth.user.customer.last4 }}
                    </span>
                  </span>
                  <span v-if="!!cardExpirationDate" test-id="card_exp_text">
                    <span>, Exp </span>
                    <span class="has-text-weight-bold" test-id="has_card_exp_date">
                      {{ cardExpirationDate }}
                    </span>
                  </span>
                </div>
              </div>
              <span v-else test-id="has_card_text">No card on file</span>
            </div>
          </div>
          <div class="button-wrapper" test-id="update_payment">
            <LsButton
              bg-color="primary-light"
              text-color="primary"
              text="update payment details"
              track-id="profile_view-update_payment"
              @click="openPayment"
            />
          </div>
        </div>
      </div>

      <div class="column ls-right-column">
        <div test-id="notification_preferences">
          <h1>Notification Preferences</h1>
          <NotificationSettings />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LsInlineEdit, LsButton, LsIcon } from '@/components/common';
import { mapState, mapActions, mapGetters } from 'vuex';
import { AUTH__IS_CUSTOMER_LEAD, PROFILE__UPDATE_BASIC_INFO } from '@/store/types';
import { lsModalService } from 'ls-vue-common';
import { PasswordModal } from '@/modals';
import NotificationSettings from '@/views/Authorized/ProfileView/NotificationSettings.vue';
import moment from 'moment';

export default {
  name: 'ProfileView',
  components: {
    LsInlineEdit,
    LsButton,
    NotificationSettings,
    LsIcon,
  },
  data() {
    return {
      currentField: '',
    };
  },
  computed: {
    ...mapState(['auth', 'profile']),
    ...mapGetters({ isCustomerLead: [AUTH__IS_CUSTOMER_LEAD] }),
    updateOrSet() {
      return this.auth.user.has_password ? 'update' : 'set';
    },
    cardExpirationDate() {
      const { customer } = this.auth.user || {};
      return !customer.card_expiration_date
        ? ''
        : moment(customer.card_expiration_date).format('MM/YY');
    },
  },
  methods: {
    ...mapActions([PROFILE__UPDATE_BASIC_INFO]),
    async updateProfile(value, name) {
      await this[PROFILE__UPDATE_BASIC_INFO]({
        [name]: value,
      });
      this.currentField = '';
    },
    openPassword() {
      lsModalService.open({
        parent: this,
        component: PasswordModal,
        props: { type: this.updateOrSet },
      });
    },
    openPayment() {
      const name = 'profileUpdatePayment';
      this.$router.push({ name });
    },
    setCurrentField(value) {
      this.currentField = value;
    },
    // so only fields that are loading show loading state
    determineLoading(field) {
      return this.profile.loadingStatus.isUpdating && this.currentField === field;
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  padding-bottom: 20px;
  font-weight: normal;
}

.column {
  > div:not(:first-child) {
    margin-top: 20px;
  }
}

.user-info {
  border-bottom: 1px solid $primary-light;
  padding-bottom: 10px;
}

.button-wrapper {
  padding: 20px 0;
}

.cc-info-wrapper {
  border-bottom: 1px solid $primary-light;
}

.cc-info {
  padding: 20px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.cc-info > span {
  margin-left: 30px;
}

.card-div {
  display: flex;
  align-content: center;
  margin: 0 auto;
}

.cc-info-text {
  padding: 4px 0 0 6px;
  display: flex;
}

.notification-section {
  padding: 15px 0;
  border-bottom: 1px solid $primary-light;
}
.heading-text {
  display: flex;
}

.control {
  padding: 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.switch-wrapper {
  width: 7.8rem;
  height: 2.8rem;
}

.bottom-border {
  border-bottom: 1px solid $primary-light;
}

@media (max-width: $mobile-max-width) {
  .cc-info {
    flex-direction: column;
    align-items: flex-start;
  }

  .card-div {
    padding-top: 6px;
    margin: 0;
  }

  h1 {
    padding-bottom: 0px;
  }
}
.themed-app {
  .user-info {
    border-bottom: 1px solid $theme-color-primary-light;
  }
  .cc-info-wrapper {
    border-bottom: 1px solid $theme-color-primary-light;
  }
  .notification-section {
    border-bottom: 1px solid $theme-color-primary-light;
  }
  .bottom-border {
    border-bottom: 1px solid $theme-color-primary-light;
  }
}
</style>
